/**
 * Amounts cross the wire as integers in minor units, so the browser never parses a
 * decimal into an IEEE-754 double. Conversion to a display string happens here and
 * nowhere else.
 */

const digitsByCurrency = new Map<string, number>()

/** Minor-unit digits for a currency: 2 for USD, 0 for JPY, 3 for BHD. */
function minorUnitDigits(currency: string): number {
  const cached = digitsByCurrency.get(currency)
  if (cached !== undefined) return cached
  const digits =
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
      .maximumFractionDigits ?? 2
  digitsByCurrency.set(currency, digits)
  return digits
}

/** Minor units to a decimal number, for charting only — never for arithmetic on money. */
export function toMajor(minor: number, currency: string): number {
  return minor / 10 ** minorUnitDigits(currency)
}

/**
 * A typed decimal amount back to exact minor units.
 *
 * Rounds rather than truncates, and goes via a string to avoid a float artefact turning
 * 49.99 into 4998. Returns null when the text is not a number, so the caller can decide
 * what to do rather than silently receiving zero.
 */
export function toMinor(value: string, currency: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '' || !/^-?\d*([.,]\d*)?$/.test(trimmed)) return null
  const parsed = Number(trimmed.replace(',', '.'))
  if (!Number.isFinite(parsed)) return null
  return Math.round(parsed * 10 ** minorUnitDigits(currency))
}

export function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    toMajor(minor, currency),
  )
}

/**
 * Axis labels: "$12.4k" rather than "$12,400.00".
 *
 * Takes a major-unit value because axis ticks are computed in chart space, where the
 * amount is already a plain number rather than an exact minor-unit integer.
 */
export function formatCompactMajor(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

export function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatDateShort(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}
